import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productsAPI } from '../lib/api';
import SiteSections from '../components/SiteSections';
import ProductCard from '../components/ProductCard';
import Seo from '../components/Seo';
import Hero from '../components/Hero';
import PaperBale from '../components/PaperBale';
import SeasonalBadge, { toneBackground } from '../components/SeasonalBadge';
import { CURRENT_SEASON } from '../data/season';
import LaurelSeal from '../components/LaurelSeal';
import PerforatedDivider from '../components/PerforatedDivider';
import TornEdge from '../components/TornEdge';
import BuyWithoutAccount from '../components/BuyWithoutAccount';
import StatsBand from '../components/StatsBand';
import ShippingCoverage from '../components/ShippingCoverage';
import SectorPicker from '../components/SectorPicker';
import FaqSection from '../components/FaqSection';
import { faqSchema } from '../lib/productFaq';
import { bestPricePerKgId } from '../lib/highlights';
import { ARTICLES } from '../data/articles';
import { ArrowRight, Truck, Clock, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useMagnetic } from '../hooks/useMagnetic';
import { whatsappUrl } from '../lib/whatsapp';

/**
 * Preguntas frecuentes de la portada. Cada respuesta sale de texto que el
 * sitio ya publica (FAQ del catálogo, guías, llms.txt) o del flujo de compra
 * real (carrito y checkout) — no son afirmaciones nuevas. La portada era la
 * única página fuerte sin un solo pasaje que contestara una pregunta
 * completa, que es lo que un asistente de IA cita.
 *
 * NO se incluyen aquí certificaciones, tiempos de entrega ni facturación:
 * no están documentados en el sitio y no se inventan.
 */
const HOME_FAQ = [
    {
        q: '¿Carpepel vende papel para tortillas al mayoreo?',
        a: 'Sí. Carpepel vende papel para tortillas al mayoreo y al menudeo, con precios especiales por volumen. El papel cebolla de 18 g y el papel estraza son de los más usados para envolver tortillas por su ligereza; el papel oro de 29 g se usa también para tortillas y alimentos calientes que necesitan más resistencia.',
    },
    {
        q: '¿Qué es el papel grado alimenticio?',
        a: 'Es papel fabricado específicamente para entrar en contacto directo con alimentos, pensado para envolver comida —incluso caliente o grasosa— sin transferirle sustancias dañinas. En la guía «Qué es el papel grado alimenticio» explicamos cómo identificarlo.',
    },
    {
        q: '¿Qué gramaje de papel necesito para mi negocio?',
        a: 'Depende del peso y la grasa del producto: 16–18 g para productos ligeros y secos, 24–30 g para uso general (tacos, tortas, antojitos), y 40–49 g para producto pesado, húmedo o muy grasoso.',
    },
    {
        q: '¿Carpepel vende al mayoreo y al menudeo?',
        a: 'Sí, Carpepel vende tanto al mayoreo como al menudeo, con precios especiales por volumen para negocios.',
    },
    {
        q: '¿A qué zonas hace envíos Carpepel?',
        a: 'Carpepel envía a toda la República Mexicana, y también a Estados Unidos, Canadá y Centroamérica. Los pedidos nacionales tienen dos opciones: pagar solo el papel y coordinar el envío por WhatsApp, donde se cotiza y se cobra aparte, o cotizar el envío con tu código postal y pagar todo junto; los internacionales los cotizamos por WhatsApp, porque el costo depende del país, del peso y del trámite de aduana.',
    },
    {
        q: '¿Cómo compro en carpepel.com y cómo pago?',
        a: 'Eliges el papel en el catálogo, lo agregas al carrito y pagas con tarjeta de crédito o débito a través de Stripe, sin crear cuenta. Si eliges pagar solo el papel, al terminar te llevamos a WhatsApp para cotizar y coordinar el envío, que se cobra aparte; si prefieres, puedes cotizar el envío con tu código postal y pagarlo junto con el papel. Crear una cuenta es opcional y sirve para guardar tu historial de pedidos.',
    },
];

// El sitio como entidad propia, enlazada con el negocio de abajo por @id.
const WEBSITE_SCHEMA = {
    "@type": "WebSite",
    "@id": "https://www.carpepel.com/#website",
    url: "https://www.carpepel.com/",
    name: "Carpepel",
    description: "Tienda en línea de papel grado alimenticio al mayoreo y menudeo, con envíos a toda la República Mexicana.",
    inLanguage: "es-MX",
    publisher: { "@id": "https://www.carpepel.com/#carpepel" },
};

export default function Home() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const ctaRef = useMagnetic(0.25, 8);
    const bestPriceId = bestPricePerKgId(products);
    const seasonProduct = CURRENT_SEASON?.productMatch
        ? products.find((p) => CURRENT_SEASON.productMatch.test(p.name))
        : null;

    useEffect(() => {
        // Ya no se llama a /api/seed aquí: es un endpoint de siembra protegido
        // por secreto, así que devolvía 403 en CADA carga de la home — una
        // petición desperdiciada por visita y un error en la consola de todos
        // los usuarios. El catálogo se siembra una sola vez, no en cada visita.
        const init = async () => {
            try {
                const res = await productsAPI.getAll();
                setProducts(res.data);
            } catch {}
            setLoading(false);
        };
        init();
    }, []);

    const features = [
        { icon: Truck, title: 'Envío Nacional e Internacional', desc: 'Toda la República Mexicana, más Estados Unidos, Canadá y Centroamérica' },
        // Único feature con sello en vez de ícono de librería: es la afirmación
        // de confianza que más importa en el rubro (contacto con alimentos), y
        // merece leerse como una certificación, no como un ícono más del grid.
        { seal: true, title: 'Grado Alimenticio', desc: 'Todos nuestros papeles son aptos para contacto directo con alimentos' },
        { icon: Package, title: 'Mayoreo y Menudeo', desc: 'Precios especiales por volumen para tu negocio' },
        { icon: Clock, title: 'Pago Seguro', desc: 'Paga con tarjeta de crédito o débito de forma segura con Stripe' },
    ];

    return (
        <div data-testid="home-page" className="folio-sections">
            <Seo
                title="Papel Grado Alimenticio al Mayoreo para Tortillas | Carpepel"
                description="Papel para tortillas al mayoreo y papel de grado alimenticio para tacos, pan y comida: encerado, estraza y tissue. Envíos a toda México, mayoreo y menudeo."
                keywords={["papel para tortillas mayoreo", "papel grado alimenticio mayoreo", "papel de grado alimenticio", "papel grado alimenticio", "papel para tortillas", "papel para comida", "papel para alimentos", "insumos alimenticios al mayoreo", "papel encerado", "papel estraza", "papel tissue", "proveedor de papel para alimentos México"]}
                canonicalPath="/"
                schema={[{
                    // "Store" y no "Organization" a secas: Store es un subtipo
                    // de LocalBusiness, que a su vez lo es de Organization —
                    // así que no se pierde ninguna propiedad de las de abajo y
                    // sí se gana la señal local. Es la que ayuda a Google a
                    // amarrar este sitio con la ficha de Google Business
                    // Profile (ya verificada): mismo nombre, mismo teléfono y
                    // misma dirección en los dos lados es de los factores que
                    // más pesan en el paquete local, que es un ranking
                    // distinto al orgánico y no depende de la antigüedad del
                    // dominio ni de los backlinks — justo lo que a Carpepel le
                    // falta hoy.
                    //
                    // NO se declaran openingHours ni aggregateRating: no
                    // tenemos ese dato y schema inventado es riesgo de acción
                    // manual, no un atajo.
                    "@type": "Store",
                    "@id": "https://www.carpepel.com/#carpepel",
                    name: "Carpepel",
                    url: "https://www.carpepel.com",
                    logo: "https://evwixwbrpysyzeehoedy.supabase.co/storage/v1/object/public/carpepel-assets/site_logo_opt.webp",
                    image: "https://evwixwbrpysyzeehoedy.supabase.co/storage/v1/object/public/carpepel-assets/site_logo_opt.webp",
                    // Verificado en backend/server.py: Stripe Checkout corre
                    // con payment_method_types=["card"] y currency "mxn".
                    currenciesAccepted: "MXN",
                    paymentAccepted: "Tarjeta de crédito, Tarjeta de débito",
                    description: "Distribuidor mexicano de papel grado alimenticio e insumos alimenticios para envolver tortillas, tacos, pan y comida en general: papel encerado, estraza, tissue y bolsas de poliseda. Envíos a toda la República Mexicana.",
                    slogan: "El papel que envuelve el sabor de México",
                    knowsAbout: [
                        "Papel grado alimenticio",
                        "Papel para tortillas",
                        "Papel encerado",
                        "Papel estraza",
                        "Papel tissue",
                        "Insumos alimenticios",
                        "Bolsas de poliseda",
                    ],
                    sameAs: [
                        "https://www.facebook.com/carpepel",
                        "https://www.tiktok.com/@carpepel",
                    ],
                    telephone: "+52-55-8403-2954",
                    address: {
                        "@type": "PostalAddress",
                        streetAddress: "Manzana 015",
                        addressLocality: "Ojo de Agua",
                        postalCode: "55770",
                        addressRegion: "Estado de México",
                        addressCountry: "MX",
                    },
                    // Tiene que coincidir con lo que la página dice a la vista
                    // (ver ShippingCoverage): structured data que promete algo
                    // distinto al contenido visible es riesgo de acción manual
                    // de Google, no solo esfuerzo desperdiciado.
                    areaServed: [
                        { "@type": "Country", name: "México" },
                        { "@type": "City", name: "Ciudad de México" },
                        { "@type": "State", name: "Estado de México" },
                        { "@type": "Country", name: "Estados Unidos" },
                        { "@type": "Country", name: "Canadá" },
                        { "@type": "Place", name: "Centroamérica" },
                    ],
                    contactPoint: {
                        "@type": "ContactPoint",
                        telephone: "+52-55-8403-2954",
                        contactType: "sales",
                        areaServed: "MX",
                        availableLanguage: "Spanish",
                    },
                }, WEBSITE_SCHEMA, faqSchema(HOME_FAQ)]}
            />
            <Hero products={products} />

            {/* La franja de colores del hero queda ARRANCADA: la hoja de
                abajo se monta sobre ella. El margen negativo es lo que hace
                el solape; el rasgado no ocupa espacio propio en el flujo. */}
            <TornEdge color="var(--paper-2)" className="-mt-3 md:-mt-4" />

            {/* Orden de la portada = orden de la decisión de compra:
                cómo compro (sin cuenta) → qué compro → cuál me sirve →
                por qué confiar → dudas. Antes los productos llegaban a
                ~3,000 px, después de cinco bloques de contexto. */}
            <BuyWithoutAccount />

            {/* Apartados que publica el admin (promociones, avisos). */}
            <SiteSections placement="home" className="max-w-7xl mx-auto px-6 md:px-12 pt-10" />

            {/* Products */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-12" data-testid="products-section">
                {/* Primer bloque tras "Compra sin cuenta": el paso 1 hecho
                    página. Son los primeros del catálogo, no un ranking — por
                    eso el titular no dice "los más vendidos". */}
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                    <div>
                        <p className="eyebrow mb-4">Catálogo</p>
                        <h2 className="font-heading text-[clamp(1.875rem,3.6vw,2.75rem)] font-black leading-[0.98] tracking-tight text-balance" style={{ color: 'var(--ink)' }}>
                            Elige tu papel{' '}
                            <span style={{ color: 'var(--kraft-deep)' }}>y agrégalo al carrito</span>
                        </h2>
                    </div>
                    <Link to="/catalogo" className="group flex-shrink-0 text-sm font-bold uppercase tracking-widest text-[var(--kraft-deep)] flex items-center gap-2">
                        {products.length > 0 ? `Ver los ${products.length} productos` : 'Ver todos'} <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </div>
                
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[1,2,3].map(i => (
                            <div key={i} className="h-48 bg-[var(--line)] animate-pulse rounded-sm" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {products.slice(0, 6).map((product, i) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                index={i}
                                /* Se calcula sobre el catálogo COMPLETO, no sobre
                                   estos 6: si el mejor precio por kilo no está
                                   entre los destacados, aquí no se marca nada —
                                   nunca una etiqueta distinta que en el catálogo. */
                                ribbon={product.id === bestPriceId ? 'Mejor precio por kilo' : null}
                            />
                        ))}
                    </div>
                )}
            </section>

            <SectorPicker products={products} />


            {CURRENT_SEASON && <TornEdge color={toneBackground(CURRENT_SEASON.tone)} className="mt-4" />}
            {CURRENT_SEASON && (
                <SeasonalBadge
                    icon={<CURRENT_SEASON.Icon className="w-16 h-16 md:w-20 md:h-20" />}
                    eyebrow={CURRENT_SEASON.eyebrow}
                    title={CURRENT_SEASON.title}
                    text={CURRENT_SEASON.text}
                    tone={CURRENT_SEASON.tone}
                    cta={seasonProduct ? { to: `/producto/${seasonProduct.id}`, label: CURRENT_SEASON.productLabel } : null}
                />
            )}


            {/* Por qué confiar: números, ficha técnica y cobertura juntos,
                después de que el visitante ya vio qué puede comprar. */}
            <StatsBand />

            <ShippingCoverage />

            {/* Features */}
            {/* Ficha técnica: los cuatro datos en una pieza con reglas, no en
                cuatro cajas iguales (ver .spec-strip en index.css). */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14" data-testid="features-section">
                <div className="spec-strip">
                    {features.map((f, i) => (
                        <div key={i} className="spec-item reveal-on-scroll" data-testid={`feature-card-${i}`}>
                            {f.seal ? (
                                /* Sin margen inferior a propósito: el bloque mide
                                   exactamente lo mismo que el cuadro de ícono de
                                   las otras tarjetas (40px + mb-4 = 56px), así los
                                   cuatro títulos de la fila arrancan a la misma
                                   altura. El aire bajo el sello lo pone el propio
                                   SVG, que trae margen interno. */
                                <LaurelSeal className="w-14 h-14 -ml-1" color="var(--kraft-deep)">
                                    <path d="M-11,1 L-3,9 L12,-10" stroke="var(--kraft-deep)" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                </LaurelSeal>
                            ) : (
                                /* Sin caja de tinta: dentro de la ficha el cuadro negro
                                   pesaba más que el dato. El trazo kraft basta. */
                                <div className="w-10 h-10 flex items-center mb-4">
                                    <f.icon className="w-6 h-6" strokeWidth={1.5} style={{ color: 'var(--kraft-deep)' }} />
                                </div>
                            )}
                            <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-[var(--ink)] mb-2">{f.title}</h3>
                            <p className="text-sm text-[var(--ink-soft)] leading-relaxed">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </section>



            {/* Guías — bloque editorial: rompe el ritmo de tarjetas y da
                enlazado interno hacia el contenido que construye autoridad */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 pb-14" data-testid="home-guides-section">
                <PerforatedDivider />
                <div className="pt-10">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
                        <div className="md:col-span-4">
                            <p className="eyebrow mb-4">Guías prácticas</p>
                            <h2 className="font-heading text-2xl md:text-3xl font-black text-[var(--ink)] leading-[1.05] tracking-tight mb-4 text-balance">
                                Elige bien tu papel, no solo el más barato
                            </h2>
                            <p className="text-sm text-[var(--ink-soft)] leading-relaxed mb-5">
                                Lo que hemos aprendido distribuyendo a negocios de alimentos, escrito sin tecnicismos.
                            </p>
                            <Link
                                to="/guias"
                                data-testid="home-guides-link"
                                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest transition-colors duration-200"
                                style={{ color: 'var(--kraft-deep)' }}
                            >
                                Ver todas las guías <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                        <div className="md:col-span-8 md:border-l md:border-[var(--line)] md:pl-10">
                            {ARTICLES.slice(0, 3).map((article, i) => (
                                <Link
                                    key={article.slug}
                                    to={`/guias/${article.slug}`}
                                    data-testid={`home-guide-${article.slug}`}
                                    className="reveal-on-scroll group flex items-baseline justify-between gap-6 py-4 border-b border-[var(--line)] last:border-b-0 transition-colors duration-300"
                                >
                                    <div className="flex items-baseline gap-4 min-w-0">
                                        <span className="font-mono text-[11px] text-[var(--ink-dim)] flex-shrink-0">
                                            {String(i + 1).padStart(2, '0')}
                                        </span>
                                        <h3 className="font-heading text-base sm:text-lg font-bold text-[var(--ink)] leading-snug text-balance group-hover:text-[var(--kraft-deep)] transition-colors duration-300">
                                            {article.title}
                                        </h3>
                                    </div>
                                    <ArrowRight className="w-4 h-4 flex-shrink-0 text-[var(--ink-dim)] transition-all duration-300 group-hover:text-[var(--kraft-deep)] group-hover:translate-x-1" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Preguntas frecuentes: el texto de aquí es palabra por palabra el
                que describe el FAQPage schema de arriba (misma constante). */}
            <FaqSection
                className="max-w-7xl mx-auto px-6 md:px-12 pt-16"
                eyebrow="Antes de comprar"
                heading="Preguntas frecuentes sobre papel grado alimenticio"
                items={HOME_FAQ}
                testId="home-faq"
            />

            {/* CTA Banner — la paca de papel a color acompaña el cierre;
                mismo nivel de cuidado editorial que el hero, no un bloque plano. */}
            {/* Segundo rasgado, ahora al revés: es la hoja de TINTA la que se
                monta sobre el papel. El mismo gesto en los dos extremos de la
                página lo vuelve un motivo, no un truco aislado. */}
            <TornEdge color="var(--ink)" className="mt-8" />

            <section style={{ backgroundColor: 'var(--ink)' }} className="on-ink relative overflow-hidden" data-testid="cta-section">
                <span className="watermark" aria-hidden="true">Carpepel</span>
                <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-28 md:pt-20 md:pb-44 flex flex-col md:flex-row items-center justify-between gap-8">
                    <div className="flex items-center gap-6 md:gap-8">
                        <PaperBale
                            variant="full"
                            className="hidden sm:block h-20 w-20 md:h-24 md:w-24 flex-shrink-0"
                        />
                        <div>
                            <h2 className="font-heading text-2xl md:text-4xl font-black uppercase tracking-tight leading-[0.98] text-white mb-3 text-balance">
                                ¿Necesitas un pedido especial?
                            </h2>
                            <p className="text-sm" style={{ color: 'var(--ink-dim)' }}>Contáctanos para cotizaciones de alto volumen y medidas especiales</p>
                        </div>
                    </div>
                    {/* El texto pide contactar; antes el único botón llevaba al
                        catálogo. La cotización va primero, el catálogo queda
                        como salida secundaria para quien aún está explorando. */}
                    <div className="flex flex-col items-center md:items-end gap-4 flex-shrink-0">
                        <a href={whatsappUrl('Hola Carpepel, quiero cotizar un pedido especial.')} target="_blank" rel="noopener noreferrer">
                            <Button ref={ctaRef} data-testid="cta-whatsapp-btn" className="magnetic rounded-sm h-12 px-8 uppercase font-bold tracking-widest text-sm whitespace-nowrap" style={{ backgroundColor: 'var(--kraft)', color: 'var(--ink)' }}>
                                Cotizar por WhatsApp
                            </Button>
                        </a>
                        <Link to="/catalogo" data-testid="cta-catalog-btn" className="group text-xs font-bold uppercase tracking-widest flex items-center gap-2" style={{ color: 'var(--kraft)' }}>
                            O explora el catálogo <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
